import { Proposal } from "@/models/proposal.model";
import { FormValues } from "@/models/proposalForm.model";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { formatPhone } from "@/utils/formatPhone";
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
} from "@chakra-ui/react";
import { Field } from "formik";
import { useEffect } from "react";

interface PixPaymentFieldsProps {
  formik: any;
  proposal?: Proposal | null;
  personalData: FormValues["personalData"];
  isPixKeyValid: boolean;
}

export const PixPaymentFields = ({
  formik,
  proposal,
  personalData,
  isPixKeyValid,
}: PixPaymentFieldsProps) => {
  const { setFieldValue, values, errors, touched } = formik;

  const pixTypeLabels: { [key: string]: string } = {
    EMAIL: "Email",
    PHONE: "Celular",
    DOCUMENT: "CPF",
    BANK_ACCOUNT: "Dados Bancários",
  };

  function getPixTypeLabel(value?: string): string {
    return pixTypeLabels[value ?? ""] || "Desconhecido";
  }

  const getPixKeyForType = (pixType: string): string => {
    switch (pixType) {
      case "DOCUMENT":
        return proposal?.NaturalPerson?.document
          ? cpfFormatter(proposal.NaturalPerson.document)
          : "";
      case "PHONE":
        return personalData?.cellphone ? formatPhone(personalData.cellphone) : "";
      default:
        return "";
    }
  };

  useEffect(() => {
    const pixKey = getPixKeyForType(values.pixType);
    setFieldValue("pixKey", pixKey);
  }, [values.pixType, personalData, proposal]);

  const handlePixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setFieldValue("pixKey", inputValue);
  };
  return (
    <Flex
      mb={4}
      alignItems="center"
      justifyContent="center"
      flexDirection={{ base: "column", md: "row" }}
    >
      <FormControl
        isInvalid={!!errors?.pixType && !!touched?.pixType}
        mr={{ base: 0, md: 4 }}
        mb={{ base: 4, md: 0 }}
        w={{ base: "100%", md: "auto" }}
      >
        <FormLabel
          mb={2}
          sx={{ fontFamily: "Lexend Deca", fontSize: { base: "xl", md: "lg" } }}
          htmlFor="pixType"
        >
          Chave Pix através de:
        </FormLabel>
        <Select
          id="pixType"
          name="pixType"
          size="sm"
          onChange={(e) => setFieldValue("pixType", e.target.value)}
          placeholder="Selecione a forma do pix"
          maxWidth="100%"
          w={{ base: "100%", md: "350px" }}
          value={values.pixType}
          variant="flushed"
          sx={{ fontFamily: "Lexend Deca", fontSize: { base: "xl", md: "lg" } }}
        >
          {Object.keys(pixTypeLabels).map((key) => (
            <option key={key} value={key}>
              {pixTypeLabels[key]}
            </option>
          ))}
        </Select>
        <FormErrorMessage>{errors?.pixType}</FormErrorMessage>
      </FormControl>

      {values.pixType && values.pixType !== "BANK_ACCOUNT" && (
        <FormControl
          isInvalid={(!!errors?.pixKey && !!touched?.pixKey) || !isPixKeyValid}
          ml={{ base: 0, md: 4 }}
          w={{ base: "100%", md: "auto" }}
        >
          <FormLabel
            sx={{ fontFamily: "Lexend Deca" }}
            fontSize={{ base: "xl", md: "lg" }}
            htmlFor="pixKey"
          >
            {getPixTypeLabel(values.pixType)}
          </FormLabel>
          <Field
            as={Input}
            id="pixKey"
            name="pixKey"
            onChange={handlePixChange}
            type="email"
            variant="flushed"
            isReadOnly={values.pixType === "DOCUMENT" || values.pixType === "PHONE"}
            w="100%"
            sx={{
              fontFamily: "Lexend Deca",
              fontSize: { base: "xl", md: "lg" },
            }}
          />
          <FormErrorMessage>
            {errors?.pixKey ||
              (!isPixKeyValid && values.pixType === "EMAIL" && "Email inválido")}
          </FormErrorMessage>
        </FormControl>
      )}
    </Flex>
  );
};
